/**
 * Cheap, regex-based extraction of imports + exported symbols from a TS
 * source file. Deliberately not using the full TypeScript AST — that
 * package is heavy and only useful for ~2% of edge cases (re-exports
 * with destructured renames, namespace imports, etc.). The graph
 * tolerates a few false negatives there; everything important lands.
 *
 * What's covered:
 *   - `import { A, B as C } from 'X'` → import edge to 'X' for both names
 *   - `import D from 'X'`             → import edge to 'X' for D
 *   - `import * as ns from 'X'`       → import edge to 'X' (no symbol)
 *   - `import 'X'`                    → side-effect import
 *   - `export class Foo`              → symbol export 'Foo'
 *   - `export function bar`           → symbol export 'bar'
 *   - `export const baz`              → symbol export 'baz'
 *   - `export interface Bing`         → symbol export 'Bing'
 *   - `export type T`                 → symbol export 'T'
 *   - `export enum E`                 → symbol export 'E'
 *   - `export { X, Y as Z }`          → symbol exports 'X', 'Z'
 *   - `export default …`              → symbol export 'default'
 */

import type { ApiSide } from './types';

export interface ExtractedImport {
  /** Raw `from '...'` value, exactly as written in source. */
  source: string;
  /**
   * Imported identifiers. Empty for side-effect imports
   * (`import 'X'`) and for `import * as ns`.
   */
  importedNames: string[];
}

export interface ExtractedExport {
  name: string;
  /** `export default …` is recorded as name='default' with isDefault=true. */
  isDefault: boolean;
}

export interface FileExtract {
  imports: ExtractedImport[];
  exports: ExtractedExport[];
  /** Names from `this.addApi('x', …)` / `this.addApi({ name: 'x' })`. */
  apiDefines: string[];
  /** Names from `this.$api.x` and `const { a, b } = this.$api`. */
  apiUses: string[];
  /** Paths from `this.$assets.client.<method>('path')` (plus the retired
   *  `AssetManager.<method>('path')` idiom, recognized only so downstream
   *  scoring/contract checks can flag it as drift). */
  assetUses: string[];
  /** Side from a `declare module … interface {Client|Server|Shared}ApiRegistry` augmentation. */
  registrySide: ApiSide | null;
}

function dedup(xs: string[]): string[] {
  return [...new Set(xs)];
}

const RE_IMPORT_NAMED  = /import\s+(?:type\s+)?\{\s*([^}]+)\s*\}\s*from\s*['"]([^'"]+)['"]/g;
const RE_IMPORT_DEFAULT = /import\s+(?:type\s+)?([A-Za-z_$][\w$]*)\s*(?:,\s*\{\s*([^}]*)\s*\})?\s*from\s*['"]([^'"]+)['"]/g;
const RE_IMPORT_STAR    = /import\s+\*\s+as\s+[A-Za-z_$][\w$]*\s+from\s*['"]([^'"]+)['"]/g;
const RE_IMPORT_BARE    = /^import\s+['"]([^'"]+)['"]/gm;

const RE_EXPORT_DECL    = /export\s+(?:default\s+)?(?:async\s+)?(?:class|function|const|let|var|interface|type|enum)\s+([A-Za-z_$][\w$]*)/g;
const RE_EXPORT_NAMED   = /export\s+\{\s*([^}]+)\s*\}/g;
const RE_EXPORT_DEFAULT = /export\s+default\b/g;

const RE_ADDAPI_STR = /\.addApi\(\s*['"]([^'"]+)['"]/g;
const RE_ADDAPI_OBJ = /\.addApi\(\s*\{[^}]*?\bname\s*:\s*['"]([^'"]+)['"]/g;
const RE_API_USE_MEMBER = /\.\$api\.([A-Za-z_$][\w$]*)/g;
const RE_API_USE_DESTRUCTURE = /(?:const|let|var)\s*\{\s*([^}]+?)\s*\}\s*=\s*this\.\$api\b/g;
/**
 * `this.$assets.client` accessor methods that take an asset-path string
 * literal. Single source of truth: `RE_ASSET_USE` (path extraction) AND
 * scoring's hallucination check both derive from this so the two can't
 * drift. All six real accessors on this surface take a path — unlike the
 * retired AssetManager idiom, there is no non-path (folder-prefix `list()`)
 * accessor here.
 */
export const ASSET_PATH_METHODS = [
  'getImage', 'getJSON', 'getFile', 'getUrl', 'has', 'load',
] as const;
/** Every real `$assets.client` accessor. Identical to `ASSET_PATH_METHODS` on
 *  this surface (no non-path accessor exists) — kept as its own export so
 *  hallucination-whitelist call sites read as intent, not as a reuse of the
 *  path-extraction list. */
export const ASSET_MANAGER_METHODS = [...ASSET_PATH_METHODS] as const;
const RE_ASSET_USE = new RegExp(
  `\\.\\$assets\\.client\\.(?:${ASSET_PATH_METHODS.join('|')})\\(\\s*['"]([^'"]+)['"]`,
  'g',
);
/**
 * Retired idiom: bare `AssetManager.<method>('path')` from the pre-migration
 * `import { AssetManager } from '@gotti/core'` API. No longer taught — the
 * asset manifest prompt exclusively teaches `this.$assets.client.*` — but
 * still recognized here so (a) path references in any pre-migration code
 * keep resolving in the knowledge graph, and (b) scoring/contract checks can
 * flag its presence as drift instead of silently missing it.
 */
const LEGACY_ASSET_MANAGER_METHODS = [
  'getImage', 'getJSON', 'getFile', 'getUrl', 'has',
  'loadImage', 'getJSONAsync', 'getFileAsync', 'list',
] as const;
const RE_ASSET_USE_LEGACY = new RegExp(
  `\\bAssetManager\\.(?:${LEGACY_ASSET_MANAGER_METHODS.join('|')})\\(\\s*['"]([^'"]+)['"]`,
  'g',
);
const RE_REG_CLIENT = /interface\s+ClientApiRegistry\b/;
const RE_REG_SERVER = /interface\s+ServerApiRegistry\b/;
const RE_REG_SHARED = /interface\s+SharedApiRegistry\b/;

export function extractFile(content: string): FileExtract {
  // Strip block + line comments so commented-out imports don't get
  // counted. Cheap pass — keeps strings; that's fine for our regex.
  const stripped = content
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/^\s*\/\/.*$/gm, '');

  const imports: ExtractedImport[] = [];
  const seenImports = new Set<string>();
  const pushImport = (source: string, names: string[]) => {
    const key = `${source}::${names.sort().join(',')}`;
    if (seenImports.has(key)) return;
    seenImports.add(key);
    imports.push({ source, importedNames: names });
  };

  for (const m of stripped.matchAll(RE_IMPORT_NAMED)) {
    const names = m[1]
      .split(',')
      .map(s => s.trim().split(/\s+as\s+/)[0].trim())
      .filter(Boolean);
    pushImport(m[2], names);
  }
  for (const m of stripped.matchAll(RE_IMPORT_DEFAULT)) {
    // Skip the case where the "default" position is actually `{...}` —
    // that already matched RE_IMPORT_NAMED above. We only want the
    // pattern that has an identifier in the default-binding position.
    if (m[1] === '{' || m[1] === 'type') continue;
    const names = [m[1]];
    if (m[2]) {
      for (const part of m[2].split(',')) {
        const n = part.trim().split(/\s+as\s+/)[0].trim();
        if (n) names.push(n);
      }
    }
    pushImport(m[3], names);
  }
  for (const m of stripped.matchAll(RE_IMPORT_STAR)) {
    pushImport(m[1], []);
  }
  for (const m of stripped.matchAll(RE_IMPORT_BARE)) {
    pushImport(m[1], []);
  }

  const exports: ExtractedExport[] = [];
  const seenExports = new Set<string>();
  const pushExport = (name: string, isDefault: boolean) => {
    const key = `${name}::${isDefault}`;
    if (seenExports.has(key)) return;
    seenExports.add(key);
    exports.push({ name, isDefault });
  };

  for (const m of stripped.matchAll(RE_EXPORT_DECL)) {
    pushExport(m[1], /^\s*export\s+default\b/.test(m[0]));
  }
  for (const m of stripped.matchAll(RE_EXPORT_NAMED)) {
    for (const part of m[1].split(',')) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const alias = trimmed.split(/\s+as\s+/);
      const exported = (alias[1] ?? alias[0]).trim();
      if (exported && /^[A-Za-z_$][\w$]*$/.test(exported)) {
        pushExport(exported, false);
      }
    }
  }
  if (RE_EXPORT_DEFAULT.test(stripped) && !exports.some(e => e.isDefault)) {
    pushExport('default', true);
  }

  const apiDefines = dedup([
    ...[...stripped.matchAll(RE_ADDAPI_STR)].map(m => m[1]),
    ...[...stripped.matchAll(RE_ADDAPI_OBJ)].map(m => m[1]),
  ]);

  const apiUses = dedup([
    ...[...stripped.matchAll(RE_API_USE_MEMBER)].map(m => m[1]),
    ...[...stripped.matchAll(RE_API_USE_DESTRUCTURE)].flatMap(m =>
      m[1].split(',').map(tok => tok.trim().split(/[:=]/)[0].trim())
        .filter(n => /^[A-Za-z_$][\w$]*$/.test(n))),
  ]);

  const assetUses = dedup([
    ...[...stripped.matchAll(RE_ASSET_USE)].map(m => m[1]),
    ...[...stripped.matchAll(RE_ASSET_USE_LEGACY)].map(m => m[1]),
  ]);

  const registrySide: ApiSide | null =
    RE_REG_CLIENT.test(stripped) ? 'client'
    : RE_REG_SERVER.test(stripped) ? 'server'
    : RE_REG_SHARED.test(stripped) ? 'shared'
    : null;

  return { imports, exports, apiDefines, apiUses, assetUses, registrySide };
}

/**
 * Compute the normalized project-relative BASE path (no extension, no
 * existence check) an import specifier resolves to. Returns null for a
 * specifier that isn't project-internal (bare package name, `@gotti/...`,
 * etc). This is the pure "where does this text point" half of
 * `resolveImportPath`, split out so a caller that already KNOWS the
 * specifier resolves to no existing file (e.g. `computeMissingImportTargets`
 * in `fixPlanner/pathUtils.ts`) can still compute the CORRECT canonical path
 * — directory-aware for relative specifiers — instead of a cruder guess that
 * only strips the leading `./`/`../` and ignores which file owns the import
 * (which is wrong for any importer not at the project root).
 *
 * Rules — identical to `resolveImportPath`'s base computation:
 *   - Workspace alias (`@/foo/bar`) → `foo/bar` (already project-root-relative).
 *   - Relative paths ('./x', '../y') resolve against the importer's dir.
 *   - Absolute project paths ('client/foo') pass through unchanged.
 */
export function resolveImportBase(importerPath: string, importSpecifier: string): string | null {
  // Workspace alias FIRST — must be checked before the bare-@ reject below.
  const isWorkspaceAlias = importSpecifier.startsWith('@/');
  const isRelative = importSpecifier.startsWith('./') || importSpecifier.startsWith('../');
  const isAbsoluteProject =
    !isRelative &&
    !isWorkspaceAlias &&
    !importSpecifier.startsWith('@') &&
    /^(client|server|shared|design|apps|packages)\//.test(importSpecifier);

  if (!isRelative && !isAbsoluteProject && !isWorkspaceAlias) return null;

  if (isWorkspaceAlias) return importSpecifier.slice(2).replace(/^\/+/, '');

  if (isRelative) {
    const dir = importerPath.split('/').slice(0, -1).join('/');
    const segments: string[] = [];
    for (const part of (dir ? dir.split('/') : []).concat(importSpecifier.split('/'))) {
      if (part === '' || part === '.') continue;
      if (part === '..') {
        segments.pop();
        continue;
      }
      segments.push(part);
    }
    return segments.join('/');
  }

  return importSpecifier.replace(/^\//, '');
}

/**
 * Resolve an `import 'X'` source to a project file path.
 *
 * Rules:
 *   - Workspace alias (`@/foo/bar`) → resolved as if it were `foo/bar`.
 *     Matches sandbox-core/src/moduleResolver.ts's behaviour so the KG
 *     stays consistent with what the sandbox runtime actually loads.
 *   - Relative paths ('./x', '../y') resolve against the importer's dir,
 *     trying .ts, .tsx, /index.ts in that order.
 *   - Absolute project paths ('client/foo') match directly with the
 *     same extension dance.
 *   - Other bare specifiers ('react', '@gotti/...') are external — return
 *     null so the linker tags the edge as external/dangling.
 */
export function resolveImportPath(
  importerPath: string,
  importSpecifier: string,
  knownFiles: Set<string>,
): string | null {
  const base = resolveImportBase(importerPath, importSpecifier);
  if (base === null) return null;

  const candidates = [
    base,
    `${base}.ts`,
    `${base}.tsx`,
    `${base}/index.ts`,
    `${base}/index.tsx`,
  ];
  for (const c of candidates) {
    if (knownFiles.has(c)) return c;
  }
  return null;
}
